import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Project } from '../../store/types'
import { useGetProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import { AutocompleteFetch } from '../Autocomplete/AutocompleteFetch'

interface AutocompleteProjectsProps {
  selectedProjects: Project[]
  setSelectedProjects: (project: Project[]) => void
}

const AutocompleteProjects: React.FC<AutocompleteProjectsProps> = ({
  selectedProjects,
  setSelectedProjects
}) => {
  const [page, setPage] = useState(0)
  const [displayedProjects, setDisplayedProjects] = useState<Map<string, Project>>(new Map())
  const [searchTerm, setSearchTerm] = useState<string>('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  const projectProps = useCallback(() => {
    return {
      page: page,
      size: 4,
      acronym: searchTerm,
      loggable: true,
    }
  }, [page, searchTerm])

  const { data: projectsData } = useGetProjectsQuery(projectProps())

  useEffect(() => {
    if (projectsData) {
      setDisplayedProjects(prev => {
        const updated = new Map(prev)
        projectsData.content.forEach(project => {
          if (!updated.has(project.id)) {
            updated.set(project.id, project)
          }
        })
        return updated
      })
    }
  }, [projectsData, page, searchTerm])

  useEffect(() => {
    if (projectsData?.content) {
      if (!projectsData.last) {
        setPage(prev => prev + 1)
      }
    }
  }, [projectsData])

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const newSearchTerm = event.target.value
      setSearchTerm(newSearchTerm)
      setPage(0)
      if (newSearchTerm === '') {
        setDisplayedProjects(new Map())
      }
    }, 500)
  }, [])

  const options = useMemo(() => {
    return Array.from(displayedProjects.values())
  }, [displayedProjects])

  return (
    <AutocompleteFetch<Project>
      selectedItems={selectedProjects}
      setSelectedItems={setSelectedProjects}
      options={options}
      getLabel={(project) => `${project.name}`}
      getId={(project) => project.id}
      labelTranslationKey="components.notificationSidebar.missingHoursNotification.selectProject"
      onSearchChange={handleSearchInputChange}
    />
  )
}

export default AutocompleteProjects